import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import isEqual from 'lodash/isEqual';
import i18n from '~shared/utils/i18n';
import CountDownTimer from '~shared/components/react/CountDownTimer';
import SelectionColumn from '~shared/components/react/ThreeMarketBetCard/SelectionColumn';
import BetButton from '~spa/Components/BetButton';
import { getSelectionLabel } from '~shared/utils/selections-utils';
import './styles/bet-card.scss';

const propTypes = {
    selections: PropTypes.array.isRequired,
    description: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.node,
    ]).isRequired,
    timer: PropTypes.string,
    scoreBoard: PropTypes.object,
    showSelectionLabels: PropTypes.bool,
    betable: PropTypes.bool.isRequired,
    isLive: PropTypes.bool,
    gtmAttribute: PropTypes.string,
    event: PropTypes.shape({
        sport_slug: PropTypes.string,
    }),
    betCardHeaderElement: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.node,
    ]),
    betCardFooterElement: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.node,
    ]),
    usesThreeMarketView: PropTypes.bool,
    isUsFormat: PropTypes.bool,
    marketOrder: PropTypes.array,
    markets: PropTypes.shape({
        market_id: PropTypes.shape({
            betable: PropTypes.bool,
            id: PropTypes.number,
            is_extra_time: PropTypes.bool,
            name: PropTypes.string,
            selection_schema: PropTypes.number,
            selections: PropTypes.object,
        }),
    }),
};

const defaultProps = {
    event: null,
    betCardHeaderElement: null,
    betCardFooterElement: null,
    usesThreeMarketView: false,
    isUsFormat: false,
    marketOrder: [],
    markets: {},
};

class BetCard extends React.Component {
    shouldComponentUpdate(nextProps) {
        return !isEqual(this.props, nextProps);
    }

    static generateTimer(time, isLive) {
        let content;

        if (isLive) {
            content = <div className="bet-card__live-tag">{i18n.t('sport_event.live')}</div>;
        } else {
            content = <CountDownTimer scheduledTime={time} />;
        }

        return (
            <div className="bet-card__timer">
                {content}
            </div>
        );
    }

    render() {
        const {
            timer,
            isLive,
            gtmAttribute,
            usesThreeMarketView = false,
            marketOrder = [],
            markets = {},
            isUsFormat = false,
            betable,
            selections,
            showSelectionLabels,
            description,
            betCardFooterElement,
            betCardHeaderElement,
        } = this.props;
        const [firstMarketId] = Object.keys(markets);
        const isExtraTime = markets[firstMarketId]?.is_extra_time ?? false;
        const betButtons = usesThreeMarketView
            ? marketOrder.map(marketId =>
                /**
                 * the market order is determined by the order of
                 * extra_default_market_id_1, extra_default_market_id_2, extra_default_market_id_3
                 * in the 'sports' table of the DB.
                 */
                <SelectionColumn
                    key={marketId}
                    isUsFormat={isUsFormat}
                    market={markets[marketId]}
                    gtmAttribute={gtmAttribute}
                />)
            : selections.map((selection, key) => {
                const {
                    id,
                    special_odds_value = false,
                    active = true,
                    tradable = true,
                    betable: selectionBetable = true,
                    sp_only = false,
                    decimal_price,
                    fraction_price,
                    american_price,
                    sport_slug = '',
                } = selection;
                const showSpecialOddsValue = Boolean(special_odds_value);
                const label = (isExtraTime || showSelectionLabels || showSpecialOddsValue)
                        ? getSelectionLabel({ selection, isExtraTime })
                        : null;
                const betButtonProps = {
                    selection_id: id ? String(id) : null,
                    active: Boolean(Number(active)),
                    tradable: Boolean(Number(tradable)),
                    betable: betable && Boolean(Number(selectionBetable)),
                    sp_only: Boolean(Number(sp_only)),
                    label,
                    highlightPriceUpdate: true,
                    gtmAttribute,
                    decimal_price,
                    fraction_price,
                    american_price,
                    sport_slug,
                };

                return <BetButton
                    key={`${id}-${key}`}
                    {...betButtonProps}
                />;
            });

        const classThreeMarketViewBody = classNames('bet-card__body', {
            'bet-card__body--three_market_view': usesThreeMarketView,
        });
        const classThreeMarketViewLabel = classNames('bet-card__label', {
            'bet-card__label--three_market_view': usesThreeMarketView,
        });

        return (
            <div className="bet-card">
                {betCardHeaderElement && (
                    <div className="bet-card__header">
                        {betCardHeaderElement}
                    </div>
                )}
                <div className={classThreeMarketViewBody}>
                    <div className="bet-card__details">
                        {timer
                            ? BetCard.generateTimer(timer, isLive)
                            : null
                        }
                        <span className={classThreeMarketViewLabel}>
                            {description}
                        </span>
                    </div>
                </div>
                <div className="bet-card__bet-buttons">
                    {betButtons}
                </div>
                {betCardFooterElement
                    ? <div className="bet-card__footer">
                        {betCardFooterElement}
                    </div>
                    : null
                }
            </div>
        );
    }
}

BetCard.propTypes = propTypes;
BetCard.defaultProps = defaultProps;

export default BetCard;
